import { ApolloError } from '@apollo/client';

import { warsawDaysAgoIso } from 'core/utils/zoomTime';

/**
 * ТЗ 0117 — аналитика звонков Aplora в карточке лида: словари и разбор
 * свободных JSON-полей (`GenericScalar`) копии карточки, разбора звонка и
 * транскрипта. Чистые функции — без React, их проверяют тесты.
 *
 * JSON приходит из чужой системы: каждое поле может отсутствовать или быть
 * другого вида. Разбор терпимый — незнакомое молча пропускается, а не роняет
 * вкладку.
 */

/** Коды отказа живых запросов и черновика (`extensions.code` / `message`). */
export const APLORA_ERROR_CODES = [
  'aplora_disabled',
  'not_found',
  'not_analyzed',
  'aplora_unavailable',
  'aplora_rate_limited',
  'unknown',
] as const;
export type AploraErrorCode = typeof APLORA_ERROR_CODES[number];

/** Ярус шанса: первая волна обзвона, вторая, остальные. */
export const APLORA_TIERS = ['first', 'second', 'rest'] as const;
export const APLORA_CATEGORIES = ['A', 'B', 'C', 'DQ'] as const;
export const APLORA_TEMPERATURES = ['HOT', 'WARM', 'COLD'] as const;
/** Статус сделки в Aplora. */
export const APLORA_STATUSES = ['active', 'won', 'lost'] as const;
/** Ярлык оценки звонка. */
export const APLORA_BANDS = ['good', 'average', 'poor'] as const;
/** Компоненты L.E.A.R.N. — в порядке букв. */
export const APLORA_LEARN_KEYS = [
  'listen',
  'empathize',
  'ask',
  'resolve',
  'next_steps',
] as const;
export type AploraLearnKey = typeof APLORA_LEARN_KEYS[number];
export const APLORA_TRENDS = ['up', 'down', 'stable'] as const;
export type AploraTrend = typeof APLORA_TRENDS[number];

export type AploraTone = 'ok' | 'warn' | 'danger' | 'info' | 'neutral';

const isOneOf = <T extends string>(
  list: ReadonlyArray<T>,
  value: unknown,
): value is T => typeof value === 'string' && list.includes(value as T);

/** Значение из словаря или `null` — незнакомое не превращается в сырой ключ. */
export const aploraKnown = <T extends string>(
  list: ReadonlyArray<T>,
  value: string | null | undefined,
): T | null => (isOneOf(list, value) ? value : null);

export const APLORA_CATEGORY_TONES: Readonly<Record<string, AploraTone>> = {
  A: 'ok',
  B: 'info',
  C: 'warn',
  DQ: 'danger',
};

export const APLORA_TEMPERATURE_TONES: Readonly<Record<string, AploraTone>> = {
  HOT: 'danger',
  WARM: 'warn',
  COLD: 'info',
};

export const APLORA_BAND_TONES: Readonly<Record<string, AploraTone>> = {
  good: 'ok',
  average: 'warn',
  poor: 'danger',
};

/**
 * ТЗ 0120 — «высокий шанс»: Aplora оценивает сделку не ниже 70 %. Один
 * порог на всё: фильтр списка, 🔥 в шапке колонки, выделение карточки.
 */
export const APLORA_HIGH_WIN_CHANCE = 0.7;

/** Шанс (0…1) не ниже порога «высокого». */
export const aploraIsHigh = (value: number | null | undefined): boolean =>
  typeof value === 'number' &&
  Number.isFinite(value) &&
  value >= APLORA_HIGH_WIN_CHANCE;

/**
 * ТЗ 0120 — выделять «высокий шанс» можно только у ОТКРЫТОГО лида: у
 * закрытого (успешно / не реализовано, удалён) шанс уже ничего не значит.
 */
export const leadIsHot = (lead: {
  aploraWinChance?: number | null;
  isClosed?: boolean | null;
  isDeleted?: boolean | null;
}): boolean =>
  !lead.isClosed && !lead.isDeleted && aploraIsHigh(lead.aploraWinChance);

/** Сделка закрыта: шанс 0 или 100 %, яруса нет. */
export const aploraIsClosed = (status: string | null | undefined): boolean =>
  status === 'won' || status === 'lost';

/** Доля 0…1 → целые проценты; не число — `null`. */
export const aploraPercent = (value: number | null | undefined) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return Math.round(Math.min(Math.max(value, 0), 1) * 100);
};

/** Балл одним знаком после запятой без лишнего «,0». */
export const aploraScore = (value: number | null | undefined) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return String(Math.round(value * 10) / 10);
};

/** Длительность звонка `м:сс` (час и больше — `ч:мм:сс`). */
export const aploraDuration = (seconds: number | null | undefined) => {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0)
    return null;
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
};

/* ── Саммари: только **жирный** ─────────────────────────────────────── */

export interface IAploraTextPart {
  text: string;
  bold: boolean;
}

/**
 * `summaryMd` → куски текста: `**…**` — жирный, остальное как есть. HTML не
 * разбирается вовсе — куски рисуются текстом React, разметка из чужой
 * системы в страницу не попадает. Непарная `**` остаётся текстом.
 */
export const aploraBoldParts = (
  source: string | null | undefined,
): IAploraTextPart[] => {
  const text = source || '';
  const parts: IAploraTextPart[] = [];
  const re = /\*\*([^*]+?)\*\*/g;
  let last = 0;
  let match = re.exec(text);
  while (match !== null) {
    if (match.index > last) {
      parts.push({ text: text.slice(last, match.index), bold: false });
    }
    parts.push({ text: match[1], bold: true });
    last = match.index + match[0].length;
    match = re.exec(text);
  }
  if (last < text.length) parts.push({ text: text.slice(last), bold: false });
  return parts;
};

/** Один и тот же текст без учёта `**`, пробелов и регистра. */
const plainKey = (value: string | null | undefined) =>
  (value || '').replace(/\*\*/g, '').replace(/\s+/g, ' ').trim().toLowerCase();

/**
 * Итог сделки, если он не повторяет саммари: у закрытой сделки Aplora часто
 * кладёт в оба поля один текст — второй раз его не показываем.
 */
export const aploraDistinctText = (
  text: string | null | undefined,
  ...others: Array<string | null | undefined>
): string | null => {
  const key = plainKey(text);
  if (!key) return null;
  return others.some((other) => plainKey(other) === key) ? null : text || null;
};

/* ── Свободные списки (риски, ошибки, рекомендации…) ────────────────── */

export interface IAploraItem {
  title: string | null;
  text: string | null;
  /** Цитата клиента/менеджера из звонка. */
  quote: string | null;
  /** Влияние 0…100, если Aplora его дала. */
  influence: number | null;
  primary: boolean;
}

type IRecord = Record<string, unknown>;

const asRecord = (value: unknown): IRecord | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as IRecord)
    : null;

const str = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

const num = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const firstStr = (record: IRecord, keys: string[]): string | null => {
  for (let i = 0; i < keys.length; i += 1) {
    const value = str(record[keys[i]]);
    if (value) return value;
  }
  return null;
};

const TITLE_KEYS = ['title', 'name', 'risk', 'label', 'action', 'flag', 'slug'];
const TEXT_KEYS = [
  'description',
  'explanation',
  'recommendation',
  'rationale',
  'reasoning',
  'text',
  'comment',
  'summary',
  'details',
];

const toItem = (value: unknown): IAploraItem | null => {
  const plain = str(value);
  if (plain) {
    return {
      title: null,
      text: plain,
      quote: null,
      influence: null,
      primary: false,
    };
  }
  const record = asRecord(value);
  if (!record) return null;
  const title = firstStr(record, TITLE_KEYS);
  const text = firstStr(record, TEXT_KEYS);
  if (!title && !text) return null;
  return {
    title,
    text: text === title ? null : text,
    quote: str(record.quote) || str(record.evidence_text),
    influence: num(record.influence),
    primary: record.is_primary === true,
  };
};

/** Список строк или объектов → пункты; не список — пусто. */
export const aploraItems = (value: unknown): IAploraItem[] => {
  if (!Array.isArray(value)) {
    const single = toItem(value);
    return single ? [single] : [];
  }
  return value.map(toItem).filter((item): item is IAploraItem => item !== null);
};

/** Красные флаги разбора: жёсткие, мягкие, авто-дисквалификация, итог. */
export interface IAploraRedFlags {
  hard: IAploraItem[];
  soft: IAploraItem[];
  autoDq: string | null;
  summary: string | null;
}

export const aploraRedFlags = (value: unknown): IAploraRedFlags => {
  if (Array.isArray(value)) {
    return { hard: aploraItems(value), soft: [], autoDq: null, summary: null };
  }
  const record = asRecord(value);
  if (!record) return { hard: [], soft: [], autoDq: null, summary: null };
  const autoDq = asRecord(record.auto_dq);
  return {
    hard: aploraItems(record.hard_flags),
    soft: aploraItems(record.soft_flags),
    autoDq:
      autoDq && autoDq.triggered === true
        ? str(autoDq.reasoning) || str(autoDq.flag_slug) || ''
        : null,
    summary: str(record.summary),
  };
};

/* ── L.E.A.R.N. ─────────────────────────────────────────────────────── */

export interface IAploraLearnPart {
  key: AploraLearnKey;
  avg: number | null;
  trend: AploraTrend | null;
}

export interface IAploraLearn {
  overall: number | null;
  callsAnalyzed: number | null;
  parts: IAploraLearnPart[];
  /** Что сделать дальше — пункты Aplora. */
  actionItems: string[];
  /** Чего не хватает для квалификации. */
  gaps: string[];
}

const strList = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.map(str).filter((item): item is string => Boolean(item))
    : [];

export const aploraLearn = (value: unknown): IAploraLearn | null => {
  const record = asRecord(value);
  if (!record) return null;
  const components = asRecord(record.components) || {};
  const parts = APLORA_LEARN_KEYS.map((key) => {
    const part = asRecord(components[key]);
    return {
      key,
      avg: part ? num(part.avg) : null,
      trend: part ? aploraKnown(APLORA_TRENDS, str(part.trend)) : null,
    };
  }).filter((part) => part.avg !== null || part.trend !== null);
  const nextSteps = asRecord(record.next_steps) || {};
  const learn = {
    overall: num(record.overall_score),
    callsAnalyzed: num(record.calls_analyzed),
    parts,
    actionItems: strList(nextSteps.action_items),
    gaps: strList(nextSteps.critical_gaps),
  };
  const empty =
    learn.overall === null &&
    !parts.length &&
    !learn.actionItems.length &&
    !learn.gaps.length;
  return empty ? null : learn;
};

/* ── Итог сделки ────────────────────────────────────────────────────── */

export interface IAploraMoney {
  amount: number;
  currency: string | null;
}

export const aploraMoney = (value: unknown): IAploraMoney | null => {
  const record = asRecord(value);
  const amount = record ? num(record.amount) : null;
  if (!record || amount === null || amount <= 0) return null;
  return { amount, currency: str(record.currency) };
};

/** Причина итога из `outcome` (`{outcome, reason, …}`). */
export const aploraOutcomeReason = (value: unknown): string | null => {
  const record = asRecord(value);
  return record ? str(record.reason) : null;
};

/* ── Транскрипт ─────────────────────────────────────────────────────── */

export interface IAploraTranscriptLine {
  at: string | null;
  speaker: string | null;
  text: string;
}

export interface IAploraTranscript {
  lines: IAploraTranscriptLine[];
  /** Текст целиком, если реплик по времени нет. */
  text: string | null;
}

export const aploraTranscript = (value: unknown): IAploraTranscript => {
  const record = asRecord(value);
  if (!record) return { lines: [], text: str(value) };
  const segments = Array.isArray(record.segments) ? record.segments : [];
  const lines = segments
    .map((segment): IAploraTranscriptLine | null => {
      const item = asRecord(segment);
      const text = item ? str(item.text) : null;
      if (!item || !text) return null;
      const start = num(item.start);
      return {
        at: start === null ? null : aploraDuration(start),
        speaker: str(item.speaker) || str(item.role),
        text,
      };
    })
    .filter((line): line is IAploraTranscriptLine => line !== null);
  return {
    lines,
    text: lines.length
      ? null
      : str(record.processed_text) || str(record.raw_text) || null,
  };
};

/* ── Ошибки ─────────────────────────────────────────────────────────── */

/** Код отказа из ответа (код или текст) — знакомый, иначе `unknown`. */
export const aploraErrorCode = (
  code: string | null | undefined,
): AploraErrorCode => aploraKnown(APLORA_ERROR_CODES, code) || 'unknown';

/** Код отказа живого запроса: `extensions.code`, затем `message`. */
export const aploraQueryErrorCode = (
  error: ApolloError | null | undefined,
): AploraErrorCode | null => {
  if (!error) return null;
  const first = error.graphQLErrors[0];
  if (!first) return 'aplora_unavailable';
  const ext = first.extensions?.code;
  return aploraErrorCode(
    typeof ext === 'string' && isOneOf(APLORA_ERROR_CODES, ext)
      ? ext
      : first.message,
  );
};

/**
 * Письмо, которое карточка лида просит открыть во вкладке «Переписка» с
 * готовыми темой и текстом (черновик Aplora). `request` растёт с каждой
 * просьбой: второй черновик — снова просьба.
 *
 * ТЗ 0120: ИИ-письмо приходит HTML (`html` главнее `text`), с id черновика
 * (`aiDraftId` уходит с отправкой) и его видом (`aiKind` — бейдж формы).
 */
export interface ILeadMailPrefill {
  subject: string;
  text: string;
  html?: string | null;
  aiDraftId?: string | null;
  aiKind?: LeadEmailAiKind | null;
  /** С чем черновик написан — «Сгенерировать заново» повторяет то же. */
  aiContractId?: string | null;
  aiHint?: string;
  request: number;
}

/* ── ТЗ 0120: «О клиенте» и полоса-сводка ───────────────────────────── */

/** Следующий контакт относительно сегодняшнего дня по Варшаве. */
export type AploraContactState = 'overdue' | 'today' | 'later';

/**
 * `next_contact_date` (`YYYY-MM-DD`) → просрочен / сегодня / позже; нет даты
 * или мусор — `null`. `today` — для тестов.
 */
export const aploraContactState = (
  date: string | null | undefined,
  today: string = warsawDaysAgoIso(0),
): AploraContactState | null => {
  const day = (date || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  if (day < today) return 'overdue';
  return day === today ? 'today' : 'later';
};

/** Уровни согласия Aplora, при которых клиент СОГЛАСИЛСЯ (как на сервере). */
export const APLORA_CONSENT_AGREED = ['commitment', 'order'] as const;

export interface IAploraConsent {
  /** Клиент согласился (`commitment` / `order`). */
  agreed: boolean;
  level: string | null;
  /** Когда — день переломного звонка (ISO). */
  at: string | null;
  /** Согласие есть, а этап CRM его не отражает. */
  crmGap: boolean;
}

export const aploraConsent = (value: unknown): IAploraConsent | null => {
  const record = asRecord(value);
  if (!record || !Object.keys(record).length) return null;
  const level = str(record.highest_level);
  const point =
    asRecord(record.decisive_call) || asRecord(record.first_turning_point);
  return {
    agreed: (APLORA_CONSENT_AGREED as readonly string[]).includes(
      (level || '').toLowerCase(),
    ),
    level,
    at: point ? str(point.date) : null,
    crmGap: record.crm_gap === true,
  };
};

export interface IAploraFactor {
  name: string;
  primary: boolean;
}

/** Внешние обстоятельства «почему готов»: главное — первым. */
export const aploraExternalFactors = (value: unknown): IAploraFactor[] => {
  const list = Array.isArray(value) ? value : [];
  const out = list
    .map((item): IAploraFactor | null => {
      const record = asRecord(item);
      const name = record
        ? str(record.name) || str(record.slug)
        : str(item) || null;
      if (!name) return null;
      return { name, primary: Boolean(record && record.is_primary === true) };
    })
    .filter((item): item is IAploraFactor => item !== null);
  return [...out.filter((f) => f.primary), ...out.filter((f) => !f.primary)];
};

/** Факты профиля клиента — чипами «О клиенте». */
export const APLORA_PROFILE_FACTS = [
  'segment',
  'motivation',
  'anxiety',
  'payment',
  'channel',
] as const;
export type AploraProfileFact = typeof APLORA_PROFILE_FACTS[number];

export interface IAploraProfileFact {
  key: AploraProfileFact;
  value: string;
}

/**
 * Профиль Aplora → факты: работа и сегмент, мотивация, опасение, основание
 * оплаты, канал. Пустые пропускаются; подпись (`*_label`) главнее кода.
 */
export const aploraProfileFacts = (
  profile: unknown,
  channel?: string | null,
): IAploraProfileFact[] => {
  const record = asRecord(profile) || {};
  const pick = (...keys: string[]) => firstStr(record, keys);
  const values: Record<AploraProfileFact, string | null> = {
    segment: pick('segment_label', 'segment'),
    motivation: pick('jtbd_trigger_label', 'jtbd_trigger'),
    anxiety: pick('jtbd_anxiety_label', 'jtbd_anxiety'),
    payment: pick('payment_basis'),
    channel: str(channel) || pick('source'),
  };
  return APLORA_PROFILE_FACTS.filter((key) => values[key]).map((key) => ({
    key,
    value: values[key] as string,
  }));
};

/** Буква методики над каждой компонентой L.E.A.R.N. (не переводится). */
export const APLORA_LEARN_LETTERS: Readonly<Record<AploraLearnKey, string>> = {
  listen: 'L',
  empathize: 'E',
  ask: 'A',
  resolve: 'R',
  next_steps: 'N',
};

export interface IAploraLearnBar {
  key: AploraLearnKey;
  /** Балл как прислала Aplora. */
  value: number;
  /** Высота полоски 0…1: шкала 0–10, а если баллы больше 10 — 0–100. */
  ratio: number;
  trend: AploraTrend | null;
}

/**
 * Полоски L.E.A.R.N. из `learn.components` — ключи Aplora `listen`,
 * `empathize`, `ask`, `resolve`, `next_steps` (`{avg, trend}`; голое число
 * тоже понимается). Незнакомые компоненты пропускаются.
 */
export const aploraLearnBars = (learn: unknown): IAploraLearnBar[] => {
  const components = asRecord(asRecord(learn)?.components) || {};
  const rows = APLORA_LEARN_KEYS.map((key) => {
    const raw = components[key];
    const part = asRecord(raw);
    const value = num(raw) ?? (part ? num(part.avg) : null);
    if (value === null) return null;
    return {
      key,
      value,
      trend: part ? aploraKnown(APLORA_TRENDS, str(part.trend)) : null,
    };
  }).filter(
    (
      row,
    ): row is {
      key: AploraLearnKey;
      value: number;
      trend: AploraTrend | null;
    } => row !== null,
  );
  const scale = rows.some((row) => row.value > 10) ? 100 : 10;
  return rows.map((row) => ({
    ...row,
    ratio: Math.min(Math.max(row.value / scale, 0), 1),
  }));
};

/** ТЗ 0120 — словесная оценка балла L.E.A.R.N. (шкала 0–10). */
export const APLORA_LEARN_GRADES = [
  'excellent',
  'good',
  'average',
  'weak',
] as const;
export type AploraLearnGrade = typeof APLORA_LEARN_GRADES[number];

/** Шкала балла: Aplora присылает 0–10, иногда 0–100. */
export const aploraLearnScale = (value: number): 10 | 100 =>
  value > 10 ? 100 : 10;

/** ≥ 8 — отлично, ≥ 6 — хорошо, ≥ 4 — средне, иначе слабо (на шкале 0–10). */
export const aploraLearnGrade = (
  value: number | null | undefined,
  scale: number = 10,
): AploraLearnGrade | null => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const ten = (value / scale) * 10;
  if (ten >= 8) return 'excellent';
  if (ten >= 6) return 'good';
  if (ten >= 4) return 'average';
  return 'weak';
};

/** Общий балл L.E.A.R.N.: `overall_score`, иначе балл из скоринга. */
export const aploraLearnOverall = (
  learn: unknown,
  fallback?: number | null,
): number | null => num(asRecord(learn)?.overall_score) ?? fallback ?? null;

/* ── ТЗ 0120: ИИ-письма лиду ────────────────────────────────────────── */

/** Вид ИИ-письма: анкета дофина, договор, follow-up. */
export const LEAD_EMAIL_AI_KINDS = ['survey', 'contract', 'followup'] as const;
export type LeadEmailAiKind = typeof LEAD_EMAIL_AI_KINDS[number];

/**
 * Коды отказа `draftLeadEmail` → `leads.emailAi.errors.<code>`; незнакомый —
 * текст сервера, иначе общий.
 */
export const LEAD_EMAIL_AI_ERRORS = [
  'ai_disabled',
  'budget_exceeded',
  'bad_input',
  'no_contract',
  'in_progress',
  'internal_error',
  'FORBIDDEN',
  'NOT_YOUR_CONTRACT',
  'NOT_FOUND',
  'SHORTLINK_DISABLED',
  'WRONG_STATUS',
  'LEAD_NOT_FOUND',
  'NETWORK',
] as const;

export const leadEmailAiErrorKey = (code: string | null | undefined) =>
  code && (LEAD_EMAIL_AI_ERRORS as readonly string[]).includes(code)
    ? `leads.emailAi.errors.${code}`
    : null;

/** Договор ждёт подписи — ссылку можно слать (как `is_link_live` сервера). */
export const LEAD_CONTRACT_AWAITING = ['sent', 'opened'] as const;

/**
 * Договоры для письма «Договор» — те же, что примет сервер: договор
 * студента, отправлен или открыт, а лесенка подписи не закрыта (`closed` —
 * подписан, отклонён, отменён, истёк или ссылка уже не живая; срока ссылки
 * в строке договора лида нет — его знает только состояние лесенки). Новые
 * первыми.
 */
export const leadEmailContracts = <
  T extends {
    status: string;
    kind?: string | null;
    createdAt?: string | null;
    signingLadder?: { state: string } | null;
  }
>(
  contracts: ReadonlyArray<T>,
): T[] =>
  contracts
    .filter(
      (item) =>
        (LEAD_CONTRACT_AWAITING as readonly string[]).includes(item.status) &&
        (!item.kind || item.kind === 'student') &&
        item.signingLadder?.state !== 'closed',
    )
    .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
